import MarkdownIt from 'markdown-it'
import taskLists from 'markdown-it-task-lists'
import DOMPurify from 'dompurify'

/**
 * Markdown is the single source of truth: the Preview, HTML and Markdown panes
 * all derive from the same string, so they can never disagree with each other.
 *
 * `html: true` matters — anydoc passes through raw HTML that survived from the
 * source document, and escaping it would show users angle brackets instead of
 * content. Everything therefore goes through DOMPurify before it is handed to
 * the DOM. Sanitising here, once, keeps every consumer safe by construction.
 */
const md = new MarkdownIt('default', {
  html: true,
  linkify: true,
  breaks: false,
  typographer: false,
}).use(taskLists, { enabled: true, label: true })

/** Render GitHub-Flavored Markdown to sanitised HTML. */
export function markdownToHtml(markdown: string): string {
  if (!markdown) return ''
  return DOMPurify.sanitize(md.render(markdown), {
    ADD_ATTR: ['target', 'rel'],
    FORBID_TAGS: ['style', 'form'],
    FORBID_ATTR: ['style'],
  })
}

const BLOCK = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'body',
  'caption',
  'colgroup',
  'dd',
  'details',
  'div',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'header',
  'hr',
  'html',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'ul',
])

const VOID = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
])

/**
 * Guillemets rather than a control character: the sentinel is parked in a text
 * node, and every text node below gets whitespace-normalised and trimmed. A
 * marker containing whitespace would not survive that round trip.
 */
const preSentinel = (index: number) => `«PRE${index}»`
const PRE_SENTINEL = /«PRE(\d+)»/g

/**
 * Pretty-print HTML for the source pane. markdown-it emits one long line per
 * block, which is unreadable and impossible to diff.
 *
 * `<pre>` blocks are lifted out first and restored verbatim: their whitespace
 * *is* their content, so reindenting them would corrupt the code they hold.
 */
export function formatHtml(html: string): string {
  if (!html) return ''

  const pres: string[] = []
  const masked = html.replace(/<pre\b[\s\S]*?<\/pre>/gi, (match) => {
    pres.push(match)
    return preSentinel(pres.length - 1)
  })

  const lines: string[] = []
  let current = ''
  let depth = 0

  const indent = () => '  '.repeat(Math.max(0, depth))
  const flush = () => {
    if (current.trim()) lines.push(current)
    current = ''
  }
  const append = (text: string) => {
    current = current ? current + text : indent() + text
  }

  for (const token of masked.split(/(<[^>]+>)/g)) {
    if (!token) continue

    const tag = /^<\/?([a-zA-Z][a-zA-Z0-9-]*)/.exec(token)
    if (!tag) {
      const text = token.replace(/\s+/g, ' ')
      if (text.trim()) append(current ? text : text.trim())
      continue
    }

    const name = tag[1]!.toLowerCase()
    if (!BLOCK.has(name)) {
      append(token)
      continue
    }

    if (token.startsWith('</')) {
      depth--
      if (current) {
        current += token
        flush()
      } else {
        lines.push(indent() + token)
      }
    } else {
      flush()
      current = indent() + token
      if (!VOID.has(name) && !token.endsWith('/>')) depth++
    }
  }
  flush()

  return lines.join('\n').replace(PRE_SENTINEL, (_, index: string) => pres[Number(index)]!)
}

/** A complete, standalone HTML document — what the download button writes out. */
export function toHtmlDocument(bodyHtml: string, title: string): string {
  const safeTitle = title.replace(/[&<>]/g, (c) => `&#${c.charCodeAt(0)};`)
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${safeTitle}</title>
</head>
<body>
${bodyHtml}
</body>
</html>
`
}
